import {differenceInCalendarDays, differenceInCalendarMonths} from 'date-fns'
import cn from 'clsx'

import './Period.scss'
import {Text, Box} from 'common/components'
import {formatDate, maybePluralize, countPercentage, _differenceInCalendarDays} from 'common/helpers'

interface PeriodProps {
  className?: string
  withOverdue?: boolean
  noCaption?: boolean
  type?: PeriodType
  openDate: Date
  maturityDate: Date
}

type PeriodType = 'compact' | 'loan'

interface PointProps {
  right?: boolean
  type?: PeriodType
  error?: boolean
  date: Date
  label: string
}

interface CaptionProps {
  isOverdueError: boolean
  pastDays: number
  leftDays: number
}

const MATURITY_LABEL = 'Maturity';

export default function Period({
  className,
  type,
  withOverdue,
  noCaption,
  openDate,
  maturityDate,
}: PeriodProps) {
  const today = new Date()
  const isOverdue = today > maturityDate
  const isOverdueError = withOverdue && isOverdue
  const months =
    differenceInCalendarMonths(
      isOverdueError ? today : maturityDate,
      openDate
    ) || 1
  const monthsArray = new Array(months).fill(null)
  const days = _differenceInCalendarDays(
    isOverdueError ? today : maturityDate,
    openDate
  );
  const pastDays = isOverdueError
    ? _differenceInCalendarDays(maturityDate, openDate)
    : isOverdue
    ? days
    : _differenceInCalendarDays(today, openDate)
  const pastDaysPercent =
    !withOverdue && isOverdue
      ? 100
      : pastDays > 0
      ? countPercentage(pastDays, days)
      : 0
  const leftDays =
    !withOverdue && isOverdue
      ? 0
      : pastDays > 0
      ? differenceInCalendarDays(maturityDate, today)
      : days

  const lineElement = (
    <div className="Period-Line">
      {monthsArray.map((_, index) => (
        <div key={index} className="Period-Dash" />
      ))}
      <div className="Period-Meter" style={{width: `${pastDaysPercent}%`}} />
      {isOverdueError && (
        <div
          className="Period-Meter Period-Meter_overdue"
          style={{
            width: `${countPercentage(Math.abs(leftDays), days)}%`,
          }}
        />
      )}
    </div>
  )

  return (
    <div className={cn(className, 'Period', type && `Period_${type}`)}>
      {!noCaption && !type && (
        <Caption
          isOverdueError={isOverdueError}
          pastDays={pastDays}
          leftDays={leftDays}
        />
      )}
      <div className="Period-Block">
        <Point type={type} date={openDate} label="Open" />
        {type === 'loan' ? (
          <Caption
            isOverdueError={isOverdueError}
            pastDays={pastDays}
            leftDays={leftDays}
          />
        ) : (
          lineElement
        )}
        <Point
          right
          type={type}
          error={isOverdueError}
          date={maturityDate}
          label={MATURITY_LABEL}
        />
      </div>
      {type === 'loan' ? (
        lineElement
      ) : type ? (
        <Caption
          isOverdueError={isOverdueError}
          pastDays={pastDays}
          leftDays={leftDays}
        />
      ) : (
        <Text align="center" size="xs" weight="medium" color="grey" mt={-16}>
          {maybePluralize(days, 'day')}
        </Text>
      )}
    </div>
  )
}

const Caption = ({isOverdueError, pastDays, leftDays}: CaptionProps) => {
  return (
    <div className="Period-Caption">
      {pastDays >= 0 && (
        <Text as="span" weight="demiBold">
          {pastDays ? maybePluralize(pastDays, 'day') : 'today'}
        </Text>
      )}
      <Text
        as="span"
        weight="medium"
        color={isOverdueError ? 'redError' : 'grey'}
      >
        {leftDays < 0
          ? `+${maybePluralize(Math.abs(leftDays), 'day')} overdue`
          : `${leftDays} remaining`}
      </Text>
    </div>
  )
}

const Point = ({type, right, error, date, label}: PointProps) => {
  const isLoan = type === 'loan'
  // 今天是到期日 标红作为提示，但不是overdue
  const isMaturityDate = label === MATURITY_LABEL && differenceInCalendarDays(new Date(), date) === 0;

  const labelElement = (
    <Text
      size="xs"
      weight="medium"
      color={error ? 'redError' : 'grey'}
      mb={isLoan ? 2 : undefined}
    >
      {label}
      {error && !isMaturityDate ? ' overdue' : ''}
    </Text>
  )

  const dateElement = (
    <Text
      noWrap
      size={type ? 'xl' : '2xl'}
      weight={type ? 'demiBold' : 'medium'}
      mb={isLoan ? undefined : 2}
      color={error ? 'redError' : undefined}
    >
      {formatDate(date, 'fifth')}
    </Text>
  )

  return (
    <Box align={right ? 'right' : undefined}>
      {type === 'loan' ? (
        <>
          {labelElement}
          {dateElement}
        </>
      ) : (
        <>
          {dateElement}
          {labelElement}
        </>
      )}
    </Box>
  )
}
